import { doc, collection, query, where, getDocs, updateDoc, writeBatch } from 'firebase/firestore';
import { firestore } from '../../../context/FirebaseConfig';
import { addMinutes } from 'date-fns';
import {
  DEVICE_DOCUMENT_PATH,
  DEVICE_BALE_FEED_TIMESTAMPS_COLLECTION_PATH,
  DEVICE_BALE_FEED_TIMESTAMP_DOCUMENT_PATH,
} from '../../../shared_components/utils/FirebaseUtils';

/**
 * Custom hook to manage document operations in Firestore
 * Handles creating, updating, and closing documents
 */
export function useDocumentManagement(currentUser, activeDocument, linkedDevices, settings, shiftInfo) {
  function isButtonActive(button) {
    /* Find out if the button is active */
    if (!button || !activeDocument || !activeDocument.data) {
      return false;
    }
    // Add defensive check for material field for backwards compatibility
    const buttonMaterial = button.material || null;
    const docMaterial = activeDocument.data.material || null;

    return (
      activeDocument.data.supplier_id === button.supplier_id &&
      activeDocument.data.field === button.field &&
      docMaterial === buttonMaterial
    );
  }

  async function closeActiveDocument() {
    /* Close active document for current device and all linked devices */
    if (!currentUser?.company?.company || !currentUser?.device_id) {
      return;
    }

    await closeActiveDocumentByDeviceId(currentUser.device_id);

    // Also close the active document on linked devices
    if (linkedDevices?.length > 0) {
      await Promise.all(linkedDevices.map((deviceId) => closeActiveDocumentByDeviceId(deviceId)));
    }
  }

  async function closeActiveDocumentByDeviceId(deviceId) {
    /* Close active documents for given deviceId */
    if (!currentUser?.company?.company) {
      return;
    }

    // First get the active document ids for the device
    const activeDocumentIds = [];
    const q = query(
      collection(firestore, DEVICE_BALE_FEED_TIMESTAMPS_COLLECTION_PATH(currentUser.company.company, deviceId)),
      where('active', '==', true),
    );
    const snapshot = await getDocs(q);
    snapshot.forEach((doc) => {
      activeDocumentIds.push(doc.id);
    });

    // Close the active documents
    await closeActiveDocumentsByIds(
      deviceId,
      activeDocumentIds.map((id) => ({ id })),
    );
  }

  async function closeActiveDocumentsByIds(deviceId, closures) {
    /*
        Close all active documents (i.e. active buttons) given as [{id, endTimestamp}].
        endTimestamp should be derived from the data (e.g. the start of the superseding
        stamp) so that concurrent writers produce identical, idempotent closes; it is
        only omitted for explicit user deactivation, where "now" is the real end time.
        Uses a writeBatch so the updates are atomic on the server and the call
        works while offline (queued and replayed on reconnect).
        */
    if (!currentUser?.company?.company || closures.length === 0) {
      return;
    }

    const batch = writeBatch(firestore);
    const fallbackEndTimestamp = new Date();
    closures.forEach(({ id, endTimestamp }) => {
      const docRef = doc(
        firestore,
        DEVICE_BALE_FEED_TIMESTAMP_DOCUMENT_PATH(currentUser.company.company, deviceId, id),
      );
      batch.update(docRef, { end_timestamp: endTimestamp || fallbackEndTimestamp, active: false });
    });

    try {
      await batch.commit();
    } catch (error) {
      console.error('Error closing active documents:', error);
    }
  }

  async function openActiveDocument(supplier_id, field, material, deviceId, closeExistingActiveDocs = true) {
    /*
            Open a new active document for the device with the specified supplier, field and material.
            Close + create are committed in a single writeBatch so the snapshot listener observes
            one atomic cache update (final state). This is required for offline correctness:
            `await batch.commit()` does not resolve while offline, so splitting into two
            commits would hang before the second one could be queued.
        */
    if (!currentUser?.company?.company || !currentUser?.uid) {
      return;
    }

    const batch = writeBatch(firestore);
    // Single timestamp shared by the close and the create, so the previous stamp's
    // end_timestamp exactly equals the new stamp's start_timestamp. Other observers
    // deriving the end time from the new stamp's start then write the same value.
    const currentDate = new Date();

    if (closeExistingActiveDocs) {
      const activeQuery = query(
        collection(firestore, DEVICE_BALE_FEED_TIMESTAMPS_COLLECTION_PATH(currentUser.company.company, deviceId)),
        where('active', '==', true),
      );
      const activeSnapshot = await getDocs(activeQuery);
      activeSnapshot.forEach((activeDoc) => {
        batch.update(activeDoc.ref, { end_timestamp: currentDate, active: false });
      });
    }

    const expirationDate = addMinutes(currentDate, settings?.stamp_max_duration || 60);
    const activeDocRef = doc(
      firestore,
      DEVICE_BALE_FEED_TIMESTAMP_DOCUMENT_PATH(currentUser.company.company, deviceId, currentDate.getTime().toString()),
    );

    batch.set(activeDocRef, {
      supplier_id: supplier_id,
      field: field,
      // only include material if it exists
      ...(material !== undefined ? { material: material || null } : {}),
      user: currentUser.uid,
      start_timestamp: currentDate,
      expiration_timestamp: expirationDate,
      active: true,
      shift_info: shiftInfo,
      device_id: deviceId,
      client_id: currentUser.company.company,
    });

    try {
      await batch.commit();
    } catch (error) {
      console.error('Error opening active document:', error);
    }
  }

  async function extendStampTimeout() {
    /** Extend the stamp timeout */
    if (!activeDocument || !currentUser?.company?.company || !currentUser?.device_id) {
      return;
    }

    const newExpirationDate = addMinutes(new Date(), settings?.stamp_max_duration || 60);

    // Collect every active stamp to extend: this device's stamp + matching active
    // stamps on linked devices (same [supplier_id, field, material]). Reads must
    // happen before the batch — you can't read inside a writeBatch.
    const refsToExtend = [
      doc(
        firestore,
        DEVICE_BALE_FEED_TIMESTAMP_DOCUMENT_PATH(currentUser.company.company, currentUser.device_id, activeDocument.id),
      ),
    ];

    if (linkedDevices?.length > 0) {
      const linkedResults = await Promise.all(
        linkedDevices.map((deviceId) =>
          getDocs(
            query(
              collection(firestore, DEVICE_BALE_FEED_TIMESTAMPS_COLLECTION_PATH(currentUser.company.company, deviceId)),
              where('active', '==', true),
              where('supplier_id', '==', activeDocument.data.supplier_id),
              where('field', '==', activeDocument.data.field),
              where('material', '==', activeDocument.data.material),
            ),
          ).then((snapshot) => ({ deviceId, snapshot })),
        ),
      );

      linkedResults.forEach(({ deviceId, snapshot }) => {
        snapshot.forEach((document) => {
          refsToExtend.push(
            doc(
              firestore,
              DEVICE_BALE_FEED_TIMESTAMP_DOCUMENT_PATH(currentUser.company.company, deviceId, document.id),
            ),
          );
        });
      });
    }

    // Single atomic, offline-safe batch: extend every stamp or none.
    const batch = writeBatch(firestore);
    refsToExtend.forEach((ref) => batch.update(ref, { expiration_timestamp: newExpirationDate }));

    try {
      await batch.commit();
    } catch (error) {
      console.error('Error extending stamp timeout:', error);
    }
  }

  function updateButtonSelection(newButtonSelection, nRows, nColumns, linkedDevices = []) {
    /* Update the button selection in the firestore */
    if (!currentUser?.company?.company || !currentUser?.device_id) {
      return;
    }

    // Make sure no undefined values are in the button selection
    const newButtonSelectionWithoutUndefined = newButtonSelection.map((button) => {
      return {
        ...button,
        supplier_id: button?.supplier_id || '',
        field: button?.field || '',
        material: button?.material || '',
      };
    });

    const deviceRef = doc(firestore, DEVICE_DOCUMENT_PATH(currentUser.company.company, currentUser.device_id));
    updateDoc(deviceRef, {
      last_plant_feed_selection: {
        button_selection: newButtonSelectionWithoutUndefined,
        n_rows: nRows,
        n_columns: nColumns,
        linked_devices: linkedDevices,
      },
    });
  }

  return {
    isButtonActive,
    closeActiveDocument,
    closeActiveDocumentsByIds,
    openActiveDocument,
    extendStampTimeout,
    updateButtonSelection,
  };
}
